import type { ReactNode } from 'react'
import { Button } from './Button.tsx'
import { icons } from './icons.ts'
import { Input } from './Input.tsx'

/**
 * Editing preparations and their ingredients.
 *
 * Extracted from the simulation form because the golden set needs the same
 * screen. `DraftPreparation` and a golden-set label's preparation are the same shape
 * apart from one per-ingredient field -- `erpCode` for a simulation, the
 * region a value was read from for a label -- so this owns the fields they
 * share and hands the differing one back through `renderIngredientExtra`.
 *
 * It owns no state. Every mutation goes out through a callback, because the
 * two callers hold their data very differently: one is a draft quotation on
 * its way to a workflow, the other is a label file on disk.
 */

export interface EditableIngredient {
  readonly name: string
  readonly dose: string | null
}

export interface EditablePreparation {
  readonly form: string | null
  readonly posology: string | null
  readonly quantity: string | null
  readonly ingredients: readonly EditableIngredient[]
}

export interface PreparationEditorProps {
  readonly preparations: readonly EditablePreparation[]
  /**
   * A change to one of the preparation's own fields.
   *
   * Deliberately cannot carry `ingredients`: the ingredient list changes
   * through the dedicated callbacks below, so a caller holding a richer
   * ingredient type never has to widen it to satisfy this one.
   */
  readonly onPreparationChange: (
    index: number,
    patch: Partial<Omit<EditablePreparation, 'ingredients'>>,
  ) => void
  readonly onIngredientChange: (
    preparation: number,
    ingredient: number,
    patch: Partial<EditableIngredient>,
  ) => void
  readonly onAddPreparation?: (() => void) | undefined
  readonly onRemovePreparation?: ((index: number) => void) | undefined
  readonly onAddIngredient?: ((preparation: number) => void) | undefined
  readonly onRemoveIngredient?: ((preparation: number, ingredient: number) => void) | undefined
  /** The one field the two callers do not share. */
  readonly renderIngredientExtra?:
    ((preparation: number, ingredient: number) => ReactNode) | undefined
  /**
   * Rendered under a field, keyed by the same path `onFocusField` reports.
   *
   * Every field the label boxes gets one: the preparation's own -- form,
   * posology, volume -- as well as each ingredient's name and dose. A
   * posology is a sentence and a volume a word inside one, so their regions
   * are often the least obvious and the most worth being able to correct.
   *
   * The golden-set screen hangs a field's regions here. The simulation has no
   * regions and passes nothing, so the two callers stay one ingredient rather
   * than diverging over a feature only one of them has.
   */
  readonly renderFieldExtra?: ((path: string) => ReactNode) | undefined
  /**
   * Show one preparation instead of all of them.
   *
   * Undefined stacks them, which is what the simulation form did before there
   * was anywhere to page. A number shows that one alone, so a rail above can
   * carry the sequence and the page stops being a scroll.
   */
  readonly only?: number | undefined
  /** Fields render but cannot be changed. What a review screen wants. */
  readonly readOnly?: boolean | undefined
  /** Focus moved to a field. Lets a caller highlight it elsewhere. */
  readonly onFocusField?: ((path: string) => void) | undefined
}

export function PreparationEditor({
  preparations,
  onPreparationChange,
  onIngredientChange,
  onAddPreparation,
  onRemovePreparation,
  onAddIngredient,
  onRemoveIngredient,
  renderIngredientExtra,
  renderFieldExtra,
  only,
  readOnly = false,
  onFocusField,
}: PreparationEditorProps) {
  const shown =
    only === undefined ? preparations.map((_, i) => i) : only < preparations.length ? [only] : []

  return (
    <>
      {preparations.length === 0 && (
        <p className="text-tP text-text-weak">Nenhuma preparação foi lida.</p>
      )}

      {shown.map((f) => {
        const preparation = preparations[f]
        if (preparation === undefined) return null

        return (
          <article
            key={f}
            className="rounded-base border-stroke-weak bg-background-raised flex flex-col gap-3 border p-4"
          >
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-tPSemi text-text-strong">Preparação {f + 1}</h3>
              {onRemovePreparation !== undefined && !readOnly && (
                <Button
                  variant="tertiary"
                  tone="danger"
                  size="small"
                  leadingIcon={icons.remove}
                  aria-label={`Remover a preparação ${f + 1}`}
                  title="Remover esta preparação"
                  onClick={() => onRemovePreparation(f)}
                />
              )}
            </div>

            {/*
              The preparation's own fields, grouped. They describe the
              preparation rather than any one ingredient, and without a border
              they read as the first ingredient's fields -- which is exactly
              how somebody ends up boxing a posology onto an active.
            */}
            <fieldset className="border-stroke-weak rounded-base flex flex-col gap-3 border p-3">
              <legend className="text-tTiny text-text-weak px-1 font-semibold uppercase">
                Preparação
              </legend>
              <div className="flex flex-col gap-0.5">
                <Input
                  label="Forma farmacêutica"
                  helperText="Em que a preparação é feita: cápsulas, creme, solução."
                  value={preparation.form ?? ''}
                  readOnly={readOnly}
                  onFocus={() => onFocusField?.(`f${f}.form`)}
                  onChange={(e) => onPreparationChange(f, { form: e.target.value })}
                />
                {renderFieldExtra?.(`f${f}.form`)}
              </div>
              <div className="flex flex-col gap-0.5">
                <Input
                  label="Posologia"
                  value={preparation.posology ?? ''}
                  readOnly={readOnly}
                  onFocus={() => onFocusField?.(`f${f}.posology`)}
                  onChange={(e) => onPreparationChange(f, { posology: e.target.value })}
                />
                {renderFieldExtra?.(`f${f}.posology`)}
              </div>
              <div className="flex flex-col gap-0.5">
                <Input
                  label="Volume"
                  helperText="Quanto da preparação: 30 cápsulas, 100 g."
                  value={preparation.quantity ?? ''}
                  readOnly={readOnly}
                  onFocus={() => onFocusField?.(`f${f}.quantity`)}
                  onChange={(e) => onPreparationChange(f, { quantity: e.target.value })}
                />
                {renderFieldExtra?.(`f${f}.quantity`)}
              </div>
            </fieldset>

            <div className="flex flex-col gap-2">
              <span className="text-tSmall text-text-weak font-semibold">Ativos</span>
              {preparation.ingredients.map((ingredient, c) => (
                <fieldset
                  key={c}
                  className="border-stroke-weak rounded-base flex flex-col gap-2 border p-3"
                >
                  {/*
                    A header row rather than an absolutely-placed control.
                    Pinned to the corner, the button landed directly above the
                    dose input and read as belonging to it -- the section had
                    no height of its own for the corner to be in. Given a row,
                    the name of the thing and the control that removes it sit
                    at opposite ends of the same line, which is what says the
                    button acts on the section.
                  */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-tTiny text-text-weak font-semibold uppercase">
                      Ativo {c + 1}
                    </span>
                    {onRemoveIngredient !== undefined && !readOnly && (
                      <Button
                        variant="tertiary"
                        tone="danger"
                        size="small"
                        leadingIcon={icons.remove}
                        aria-label={`Remover o ativo ${c + 1}`}
                        title="Remover este ativo"
                        onClick={() => onRemoveIngredient(f, c)}
                      />
                    )}
                  </div>

                  {/*
                    Each field owns its coordinates, directly beneath it and
                    tight to it like helper text. Stacked at the foot of the
                    section instead, two rows of four numbers sat under two
                    inputs with nothing saying which belonged to which.
                  */}
                  <div className="flex flex-wrap items-start gap-2">
                    <div className="flex min-w-[10rem] flex-1 flex-col gap-0.5">
                      <Input
                        aria-label={`Ativo ${c + 1} da preparação ${f + 1}`}
                        placeholder="Ativo"
                        value={ingredient.name}
                        readOnly={readOnly}
                        onFocus={() => onFocusField?.(`f${f}.c${c}.name`)}
                        onChange={(e) => onIngredientChange(f, c, { name: e.target.value })}
                      />
                      {renderFieldExtra?.(`f${f}.c${c}.name`)}
                    </div>
                    <div className="flex flex-col gap-0.5">
                      <Input
                        aria-label={`Dose do ativo ${c + 1} da preparação ${f + 1}`}
                        placeholder="Dose"
                        containerClassName="w-[8rem]"
                        value={ingredient.dose ?? ''}
                        readOnly={readOnly}
                        onFocus={() => onFocusField?.(`f${f}.c${c}.dose`)}
                        onChange={(e) => onIngredientChange(f, c, { dose: e.target.value })}
                      />
                      {renderFieldExtra?.(`f${f}.c${c}.dose`)}
                    </div>
                    {renderIngredientExtra?.(f, c)}
                  </div>
                </fieldset>
              ))}

              {onAddIngredient !== undefined && !readOnly && (
                <Button
                  variant="secondary"
                  tone="neutral"
                  size="small"
                  className="self-start"
                  onClick={() => onAddIngredient(f)}
                >
                  Adicionar ativo
                </Button>
              )}
            </div>
          </article>
        )
      })}

      {onAddPreparation !== undefined && !readOnly && (
        <Button
          variant="secondary"
          tone="neutral"
          size="small"
          className="self-start"
          onClick={onAddPreparation}
        >
          Adicionar preparação
        </Button>
      )}
    </>
  )
}
